import { API_URL, getAccessToken } from "@/api/_client";
import { ChatMessageType, getChatHistory } from "@/api/chat";
import React, { useCallback } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { useAppContext } from "./app-context";
import { ChatMessagesContext } from "./chat-message-provider";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "@/api/user";

const CHAT_WEBSOCKET_URL = API_URL + "ws/chat";

export const ChatMessagesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { currentChatSessionId, useConfirmation } = useAppContext();
  const confirmation = useConfirmation();
  const queryClient = useQueryClient();
  const [usersTyping, setUsersTyping] = React.useState<
    { first_name: string; last_name: string; id: string }[]
  >([]);
  const user = useUser();
  const [messages, setMessages] = React.useState<ChatMessageType[]>([]);
  const typingTimeouts = React.useRef<Map<string, NodeJS.Timeout>>(new Map());

  const TYPING_TIMEOUT_DURATION = 10000;

  const removeUserFromTyping = useCallback((userId: string) => {
    const existingTimeout = typingTimeouts.current.get(userId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      typingTimeouts.current.delete(userId);
    }
    setUsersTyping((oldUsers) => oldUsers.filter((user) => user.id !== userId));
  }, []);

  const token = getAccessToken();
  const url = new URL(CHAT_WEBSOCKET_URL);
  url.searchParams.append("session_id", currentChatSessionId || "");
  url.searchParams.append("token", token || "");

  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(
    url.href,
    {
      shouldReconnect: () =>
        currentChatSessionId !== undefined && token !== undefined,
    },
    currentChatSessionId !== undefined && token !== undefined,
  );

  const submitEvent = useCallback(
    (event: string | object) => {
      if (currentChatSessionId && token) {
        sendJsonMessage({ event: event });
      }
    },
    [currentChatSessionId, token, sendJsonMessage],
  );

  React.useEffect(() => {
    if (!currentChatSessionId) return;
    setMessages([]);

    const fetchMessages = async () => {
      const res: ChatMessageType[] = await getChatHistory(currentChatSessionId);
      setMessages((oldMessages) => {
        if (oldMessages === undefined) return res;
        const dedupedMessages =
          res?.filter(
            (msg) => !oldMessages.some((oldMsg) => oldMsg.id === msg.id),
          ) || [];
        return [...oldMessages, ...dedupedMessages];
      });
    };

    fetchMessages();

    // Cleanup: clear all typing timeouts when chat session changes
    const timeouts = typingTimeouts.current;
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
      setUsersTyping([]);
    };
  }, [currentChatSessionId]);

  const canSendMessages = readyState === ReadyState.OPEN;

  React.useEffect(() => {
    if (!lastMessage || !lastMessage.data) return;
    // Parse the last message data and handle different message types
    const messageData = JSON.parse(lastMessage.data);
    // if the user is "typing" and just received a message, clear their typing status
    if (messageData.type === "chat_message") {
      removeUserFromTyping(messageData.user.id);
    }
    if (
      messageData.sender === "event" &&
      "is_typing" in (messageData.events || {})
    ) {
      // Handle typing events
      if (messageData.events.is_typing) {
        if (messageData.user.id === user.data?.id) {
          // If the user is typing, we don't want to add them to the typing list
          return;
        }

        // Clear any existing timeout for this user
        const existingTimeout = typingTimeouts.current.get(messageData.user.id);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        setUsersTyping((oldUsers) => {
          if (!oldUsers.map((ou) => ou.id).includes(messageData.user.id)) {
            return [...oldUsers, messageData.user];
          }
          return oldUsers;
        });

        const timeout = setTimeout(() => {
          removeUserFromTyping(messageData.user.id);
        }, TYPING_TIMEOUT_DURATION);

        typingTimeouts.current.set(messageData.user.id, timeout);
      } else {
        removeUserFromTyping(messageData.user.id);
      }
      return;
    }
    // Handle user removed event
    if (
      messageData.type === "event" &&
      messageData.events.user_removed?.length
    ) {
      messageData.events.user_removed.forEach(
        (userItem: { item: { id: string } }) => {
          if (userItem.item.id === user.data?.id) {
            setMessages([]);
            queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
            confirmation.open({
              title: "You have been removed from the chat",
              description: "You can no longer send messages in this chat.",
              confirmText: "Ok",
              hideCancel: true,
            });
          }
        },
      );
    }
    // handle messages
    setMessages((oldMessages) => {
      if (oldMessages === undefined) return [messageData];
      return [...oldMessages, messageData];
    });
    if (
      messageData.type === "event" &&
      (messageData.events.users_added?.length ||
        messageData.events.users_removed?.length)
    ) {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    }
  }, [lastMessage, queryClient, user.data?.id, removeUserFromTyping]);

  const sendMessage = useCallback(
    (message: string, useAI: boolean) => {
      if (canSendMessages) sendJsonMessage({ message: message, use_ai: useAI });
    },
    [canSendMessages, sendJsonMessage],
  );

  return (
    <ChatMessagesContext.Provider
      value={{
        canSendMessages,
        sendMessage,
        messages,
        submitEvent,
        usersTyping,
      }}
    >
      {children}
    </ChatMessagesContext.Provider>
  );
};
